import { EventUpdateResponse, UpdateEvent } from '../badges/event';
import { Company } from '../company/company';
import { Facility } from '../facility/facility';
import { AppIcon } from '../icon/icon';
import { AnyItem, Item, ItemType } from '../item/item';
import { Point, Vertices } from '../location/point';
import { Person } from '../person/person';
import { ResultType } from '../search/search';
import { Walt } from '../walt/walt';
import { Taggable } from '../core/core';

export enum MapScope {
    Account = 'account',
    // eslint-disable-next-line @typescript-eslint/no-shadow
    Facility = 'facilities',
}

export interface WeavixMap {
    name: string;
    lat: number;
    lng: number;
    zoom: number;
    bounds?: google.maps.LatLngBounds;
    id?: string;
    tags?: string[];
    crafts?: string[];
}

export interface Geofence extends Omit<Taggable, 'folderId'> {
    id?: string;
    meshId?: number;
    name: string;
    color: string;
    vertices: Vertices;
    areaRestricted?: boolean;
}

export interface MapGeofence extends Geofence {
    polygon?: google.maps.Polygon;
    listener?: google.maps.MapsEventListener;
}

export interface MapMarker {
    marker?: RichMarker;
    listener?: google.maps.MapsEventListener;
    closeListener?: google.maps.MapsEventListener;
}

export type MapPerson = Person & MapMarker & { action?: string };

export type MapItem = AnyItem & MapMarker;

export type MapWalt = Walt & MapMarker & { person?: Person };

export interface MapCompany extends Company {
    filterChecked?: boolean;
}

export interface LatLng {
    lat: number;
    lng: number;
}

export const CENTER_POS = 50; // half of the width of map label in map.scss

export interface DvrPeople {
    [id: string]: DvrPerson;
}
export interface DvrPerson extends Person {
    latestEvent: any;
}
export interface DvrItems {
    [id: string]: DvrItem;
}
export interface DvrItem extends Item {
    latestEvent: any;
}

export interface GeofenceWithEvents extends Geofence {
    events: UpdateEvent[];
}

export interface DvrState {
    facility: Facility;
    accountId: string;
    events: {
        [key: string]: EventUpdateResponse[];
    };
    range: {
        from: Date;
        to: Date;
    };
    people?: { [id: string]: DvrPerson };
    items?: { [id: string]: DvrItem };
}

export interface MarkerMaps {
    people: Map<string, Person>;
    items: Map<string, Item>;
}

export interface ClusterItem {
    id: string;
    type: FilterResultType;
}

export interface ClusterMapMarker extends google.maps.Marker {
    type: ResultType;
    id: string;
}

export const enum MapMarkerItemMode {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    Item = 'item',
}
export interface MapFilterState {
    filters: { [key in MapFilterCategory]?: boolean }; // Includes all filters except companies
    tags: string[];
    crafts: string[];
    companiesList: string[];
    itemTypes: string[];
}

export interface MapViewState {
    location: Point;
    bounds: Vertices;
    zoom: number;
}

export interface CustomMarkerOptions extends Readonly<google.maps.MarkerOptions> {
    id: string;
    type: ResultType;
    locationAccuracy?: number;
    locationDate?: Date;
}

export interface RichMarkerOptions extends CustomMarkerOptions {
    content: string;
    shadow?: boolean;
    hideCloseIcon?: boolean;
    customCalloutClose?: boolean;
    customCalloutContent?: string;
    customCalloutActions?: CustomCalloutAction[];
    removeIfClustered: boolean; // if items with this property become part of a cluster, they will be removed
    data?: any;
}

export interface CustomCalloutAction {
    name: string;
    label?: string;
    icon?: string;
    hoverIcon?: string;
    class?: string;
    show?: (obj: any) => boolean;
}

export interface RichMarker extends google.maps.Marker {
    id: string;
    locationAccuracy?: number;
    locationDate?: Date;
    circle?: google.maps.Circle;
    type: FilterResultType;
    selected?: boolean;
    getContent(): string;
    setContent(content: string): void;
}

export interface MarkerClusterer {
    addMarker(marker: google.maps.Marker, noDraw: boolean): void;
    removeMarker(marker: google.maps.Marker, noDraw: boolean): boolean;
    repaint(): void;
    getMarkers(): google.maps.Marker[];
    setMap(map: google.maps.Map): void;
    setExceptionMarkerIds(exceptionMarkerIds: { [key: string]: FilterResultType }): void;
}

export interface ClusterIconInfo {
    text: string;
    index: number;
    title: string;
}

export interface ClusterIconStyle {
    url: string;
    height: number;
    width: number;
    anchorText?: number[];
    anchorIcon?: number[];
    textColor?: string;
    textSize?: number;
    textDecoration?: string;
    fontWeight?: string;
    fontStyle?: string;
    fontFamily?: string;
    backgroundPosition?: string;
}

export interface MarkerClustererOptions {
    gridSize?: number;
    maxZoom?: number;
    zoomOnClick?: boolean;
    averageCenter?: boolean;
    minimumClusterSize?: number;
    ignoreHidden?: boolean;
    title?: string;
    calculator?: (markers: google.maps.Marker[], numStyles: number) => ClusterIconInfo;
    clusterClass?: string;
    styles?: ClusterIconStyle[];
    enableRetinaIcons?: boolean;
    batchSize?: number;
    batchSizeIE?: number;
    imagePath?: string;
    imageExtension?: string;
    imageSizes?: number[];
}

export interface MapView {
    lat: number;
    lng: number;
    bounds: google.maps.LatLngBounds;
    zoom?: number;
}

export enum MapEntityToggle {
    Items = 'items',
    Geofences = 'geofences',
}

export enum MapFilterCategory {
    People = 'people',
    Companies = 'companies',
    Crafts = 'crafts',
    Groups = 'groups',
    Geofences = 'geofences',
    HeadsUp = 'headsUp',
    HoldUp = 'holdUp',
    ItemTypes = 'itemTypes',
    Items = 'items',
    // eslint-disable-next-line @typescript-eslint/no-shadow
    Walt = 'walt',
    WaltCrafts = 'waltCrafts',
    BatteryLevel = 'batteryLevel',
    LastLocation = 'lastLocation',
    LastSync = 'lastSync',
    Version = 'version',
}

export enum FilterResultType {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    Person = 'person',
    Items = 'items',
    ItemTypes = 'itemTypes',
    Geofence = 'geofence',
    MapView = 'mapView',
    // eslint-disable-next-line @typescript-eslint/no-shadow
    Company = 'company',
    Craft = 'craft',
    Form = 'form',
    Rule = 'rule',
    Channel = 'channel',
    Tag = 'tag',
    Badge = 'badge',
    PermissionGroup = 'permissionGroup',
    // eslint-disable-next-line @typescript-eslint/no-shadow
    Walt = 'walt',
    Structure = 'structure',
    Building = 'building',
    FloorPlan = 'floorPlan',
    WaltStatus = 'waltStatus',
    WaltCraft = 'waltCraft',
    LocationReporting = 'locationReporting',
    Beacon = 'beacon',
    HeadsUp = 'headsUp',
    HoldUp = 'holdUp',
    Site = 'site',
    OnSite = 'onSite',
    Repeater = 'repeater',
    Wranglers = 'wranglers',
    DeskWrangler = 'deskWrangler',
    Model = 'model',
    BatteryLevel = 'batteryLevel',
    LastLocation = 'lastLocation',
    LastSync = 'lastSync',
    Version = 'version',
}

export const CategoryToResultType: { [key in MapFilterCategory]?: FilterResultType } = {
    [MapFilterCategory.People]: FilterResultType.Person,
    [MapFilterCategory.Companies]: FilterResultType.Company,
    [MapFilterCategory.Crafts]: FilterResultType.Craft,
    [MapFilterCategory.WaltCrafts]: FilterResultType.WaltCraft,
    [MapFilterCategory.HeadsUp]: FilterResultType.HeadsUp,
    [MapFilterCategory.HoldUp]: FilterResultType.HoldUp,
    [MapFilterCategory.Version]: FilterResultType.Version,
    [MapFilterCategory.LastLocation]: FilterResultType.LastLocation,
    [MapFilterCategory.LastSync]: FilterResultType.LastSync,
    [MapFilterCategory.BatteryLevel]: FilterResultType.BatteryLevel,
};

export type MapFilterRowDisplay = 'person' | 'item' | 'default';
export interface MapFilterResult {
    name: string;
    key: string;
    category: MapFilterCategory;
    type: FilterResultType;
    selected?: boolean;
    hidden?: boolean; // only applies to search atm
    setSelected?: (this: MapFilterResult, selected: boolean, original?: boolean, click?: boolean) => any;
    disabled?: (this: MapFilterResult) => boolean;
    appendixText?: (this: MapFilterResult) => string;
    appendixIcon?: AppIcon;
    childCount?: number;
    displayType?: MapFilterRowDisplay;
    data?: Person | Item & { itemType: ItemType, owner: Company };
    children?: { [key: string]: MapFilterResult };
    entities?: { [key: string]: MapFilterResult }; // entities you want to keep track of within category (i.e. people in company)
    filters?: { [key in MapFilterCategory]?: { [id: string]: MapFilterResult } };
}

export enum MapFilterView {
    Filter = 'filter',
    EntitySearch = 'personSearch',
    PersonDetail = 'personDetail',
    ItemDetail = 'itemDetail',
}

export enum MapMode {
    HeatMap = 'heat-map',
    Individuals = 'individuals',
    Active = 'active',
    All = 'all',
}

export const FilterResultToSearchView: { [key in MapFilterCategory]?: MapFilterView } = {
    [MapFilterCategory.People]: MapFilterView.PersonDetail,
    [MapFilterCategory.Items]: MapFilterView.ItemDetail,
};

export const MapModeButtons: { [key in MapMode]: ListButton } = {
    [MapMode.HeatMap]: {
        textKey: `shared.map.mode.${MapMode.HeatMap}`,
        keyValue: MapMode.HeatMap,
        selected: false,
    },
    [MapMode.Individuals]: {
        textKey: `shared.map.mode.${MapMode.Individuals}`,
        keyValue: MapMode.Individuals,
        selected: false,
    },
    [MapMode.Active]: {
        textKey: `shared.map.mode.${MapMode.Active}`,
        keyValue: MapMode.Active,
        selected: false,
    },
    [MapMode.All]: {
        textKey: `shared.map.mode.${MapMode.All}`,
        keyValue: MapMode.All,
        selected: false,
    },
};

export enum MapType {
    People = 'people',
    Communications = 'communications',
}

export interface FilterTree {
    category: MapFilterCategory;
    subcategories?: FilterTree[];
}
export interface FilterEntity {
    filters: { [key in MapFilterCategory]?: { [key: string]: number }; };
    weight?: number;
    id?: string;
    category?: MapFilterCategory;
    details?: Partial<MapFilterResult>;
}

export interface ListButton {
    textKey: string;
    keyValue: string;
    selected?: boolean;
}
